import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { pushDataLayer, useStores } from "../../helpers";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { forceCheck } from 'react-lazyload';

import { styled } from "../../stitches.config";

import { DESKTOP_BREAKPOINT } from "../../components/common/constants/constants";
import { ReactComponent as CloseIcon } from '../../redesign/assets/icons/close-icon.svg?react';
import NextArticle from "./NextArticle";

const CONFIG = {
    desktopScroll: window.APP_SETTINGS.feature_flags_extended?.['nextArticlePopup'].extended_value?.['desktopScroll'] ?? 3200,
    mobileScroll: window.APP_SETTINGS.feature_flags_extended?.['nextArticlePopup'].extended_value?.['mobileScroll'] ?? 2650,
    categories: window.APP_SETTINGS.feature_flags_extended?.['nextArticlePopup'].extended_value?.['categories'] ?? []
};

const SeeAlsoArticlesPopup: React.FC = observer(() => {

    const navigate = useNavigate();

    //Get next article
    const { ArticleStore, UIStore } = useStores();
    const nextArticle = useMemo(() => {
        if (ArticleStore.fetchedArticle && ArticleStore.fetchedArticle.nextArticleInCategory)
            return ArticleStore.fetchedArticle.nextArticleInCategory.article;
        return null;
    }, [ArticleStore.fetchedArticle]);

    //Manage popup state
    const popupRef = useRef<HTMLDialogElement>(null);
    const [isOpen, setOpen] = useState<boolean>(false);

    const openPopup = useCallback(() => setOpen(true), []);
    const closePopup = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!popupRef.current)
            return;

        const { current: popup } = popupRef;
        if (isOpen) {
            //@ts-ignore - we have outdated Typescript types
            popup.showModal();
            forceCheck();
            disableBodyScroll(document.body);

            const dataLayerObject = {
                event: 'virtualPageview',
                virtualPageURL: `${nextArticle.uri}?pwtc`,
                virtualPageTitle: nextArticle.seo_title,
                pageType: 'Artykuł doładowany',
                articleSection: nextArticle.category.name,
            };

            pushDataLayer(dataLayerObject);

            UIStore.trackPageView({
                page: dataLayerObject.virtualPageURL,
                title: dataLayerObject.virtualPageTitle
            });
        } else {
            //@ts-ignore - we have outdated Typescript types
            popup.close();
            enableBodyScroll(document.body);
        }
    }, [isOpen, nextArticle]);

    useEffect(() => {
        closePopup();
        // #9578 na mobile jest problem, że w połowie tekstu strona scrolluje z powrotem na górę - sprawdzamy czy to pomoże
        // window.scrollTo(0, 0);
    }, [nextArticle]);

    //Attach scroll event's to open popup
    useEffect(() => {
        const onScroll = () => {
            const isMobile = window.innerWidth < DESKTOP_BREAKPOINT;

            if ((isMobile && window.scrollY > CONFIG['mobileScroll']) || (!isMobile && window.scrollY > CONFIG['desktopScroll'])) {
                openPopup();
                window.removeEventListener('scroll', handleScroll);
                sectionOberver.disconnect();
            }
        };

        const handleScroll = () => {
            requestAnimationFrame(() => {
                setTimeout(() => onScroll(), 0);
            });
        };

        const desktopSection = !/^\d+$/.test(CONFIG['desktopScroll']) ? document.querySelector(CONFIG['desktopScroll']) : null;
        const mobileSection = !/^\d+$/.test(CONFIG['mobileScroll']) ? document.querySelector(CONFIG['mobileScroll']) : null;

        const sectionOberver = new IntersectionObserver((entries) => {
            const isMobile = window.innerWidth < DESKTOP_BREAKPOINT;

            entries.forEach((entry) => {

                if (entry.isIntersecting && ((entry.target == desktopSection && !isMobile) || ((entry.target == mobileSection && isMobile)))) {
                    openPopup();
                    window.removeEventListener('scroll', handleScroll);
                    sectionOberver.disconnect();
                }
            });

        }, {
            threshold: 0.2,
        });
        desktopSection && sectionOberver.observe(desktopSection);
        mobileSection && sectionOberver.observe(mobileSection);

        window.addEventListener('scroll', handleScroll, {
            passive: true
        });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            sectionOberver.disconnect();
        }

    }, []);

    const canRenderPopup = useMemo(() => {
        if (!nextArticle)
            return false;

        //Check if popup has categories config then check article category
        if (Array.isArray(CONFIG.categories) && CONFIG.categories.length > 0 && !CONFIG.categories.includes(ArticleStore.fetchedArticle.article.category.OID))
            return false;

        return true;
    }, [ArticleStore.fetchedArticle]);

    if (!canRenderPopup)
        return null;

    return createPortal((
        <Popup ref={popupRef}>
            <PopupContent>
                <p className="header">
                    Zobacz również
                    <PopupCloseButton onClick={closePopup}>
                        <CloseIcon />
                    </PopupCloseButton>
                </p>
                <PopupNextArticleWrapper>
                    <NextArticle
                        hideAdslots
                        articleData={nextArticle}
                        dataAnalyticsName={"next-article-popup"}
                        id={"next-article-popup"}
                        adslotNum={-1}
                    />
                </PopupNextArticleWrapper>
            </PopupContent>
        </Popup>
    ), document.body);

});

export default SeeAlsoArticlesPopup;

const Popup = styled('dialog', {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '2rem 1rem',
    margin: '0',
    width: '100%',
    height: '100vh',
    overflowY: 'auto',
    display: 'none',
    "&:modal": {
        maxWidth: "100vw",
        maxHeight: "100vh",
        display: "flex"
    },
    '&::backdrop': {
        background: "#000",
        opacity: "0.75"
    }
});

const PopupContent = styled('div', {
    background: '$grayscale0',
    padding: '2rem 1.5rem',
    width: '100%',
    margin: 'auto',
    maxWidth: "375px",
    "@bp3": {
        maxWidth: "774px"
    },
    "@bp4": {
        maxWidth: '840px',
    },
    ".header": {
        fontSize: "24px",
        lineHeight: "30px",
        fontWeight: 700,
        margin: '0 0 1rem',
        color: "$grayscale100",
        "@bp4": {
            fontSize: "28px"
        }
    }
});

const PopupNextArticleWrapper = styled('div', {
    margin: '-24px 0',
    "@bp4": {
        marginBottom: '0'
    },
    "& > .lazyload-wrapper > div": {
        border: 'none',
        minHeight: 'unset',
        color: "$grayscale100",
        "& > br": {
            display: 'none'
        }
    }
});

const PopupCloseButton = styled('button', {
    background: 'transparent !important',
    border: '0 !important',
    padding: '0 !important',
    outline: '0 !important',
    display: "block",
    float: "right",
    "& > svg ": {
        width: "24px",
        height: "24px",
        "& > path": {
            fill: "$grayscale100"
        }
    }
});